import { AxiosResponse } from 'axios';
import {
	CompanyExternalUser,
	CreateCompanyExternalUserDTO,
	UpdateCompanyExternalUserDTO,
	CompanyExternalUserFilter,
	VerifyOnboardingTokenInput,
	OnboardingTokenVerificationResponse,
	SubmitOnboardingFormInput,
	SubmitExternalOnboardingFormInput,
	OnboardingSubmissionResponse,
	UpdateOnboardingProgressInput,
	OnboardingProgressUpdateResponse,
} from '../data/models/companyExternalUser';
import axiosClient, { baseAxiosClient } from '../clients/axios-client';
import { stringifyReqObj } from '../utils/common-utils';
import Config from '../config';
import { userProfileROGraphQl } from './user-service';

export async function getCompanyExternalUsers(
	filter: CompanyExternalUserFilter = {}
): Promise<{ list: CompanyExternalUser[]; count: number }> {
	const graphqlQuery = `
		{
			companyExternalUsers(
				filter: ${stringifyReqObj(filter)},
			) {
				list {
					${companyExternalUserROGraphQL}
				}
				count
			}
		}
	`;

	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: 'POST',
		data: {
			query: graphqlQuery,
		},
	});

	return response?.data?.companyExternalUsers;
}

export async function getCompanyExternalUser(
	id: string
): Promise<CompanyExternalUser> {
	const graphqlQuery = `
		{
			companyExternalUser(id: "${id}") {
				${companyExternalUserROGraphQL}
			}
		}
	`;

	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: 'POST',
		data: {
			query: graphqlQuery,
		},
	});
	return response?.data?.companyExternalUser;
}

export async function getCompanyExternalUsersByCompany(
	companyId: string
): Promise<CompanyExternalUser[]> {
	const graphqlQuery = `
		{
			companyExternalUsers(companyId: "${companyId}") {
				${companyExternalUserROGraphQL}
			}
		}
	`;

	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: 'POST',
		data: {
			query: graphqlQuery,
		},
	});
	return response?.data?.companyExternalUsers;
}

export async function createCompanyExternalUser(
	input: CreateCompanyExternalUserDTO
): Promise<CompanyExternalUser> {
	const graphqlQuery = `
		mutation {
			createCompanyExternalUser(
				input: ${stringifyReqObj(input)},
			) {
				${companyExternalUserROGraphQL}
			}
		}
	`;

	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: 'POST',
		data: {
			query: graphqlQuery,
		},
	});
	return response?.data?.createCompanyExternalUser;
}

export async function updateCompanyExternalUser(
	id: string,
	input: UpdateCompanyExternalUserDTO
): Promise<CompanyExternalUser> {
	const graphqlQuery = `
		mutation {
			updateCompanyExternalUser(
				id: "${id}",
				input: ${stringifyReqObj(input)},
			) {
				${companyExternalUserROGraphQL}
			}
		}
	`;

	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: 'POST',
		data: {
			query: graphqlQuery,
		},
	});
	return response?.data?.updateCompanyExternalUser;
}

export async function resendExternalUserInvite(
	id: string,
	customMessage?: string
): Promise<boolean> {
	const customMessageParam = customMessage
		? `, customMessage: "${customMessage.replace(/"/g, '\\"')}"`
		: '';
	const graphqlQuery = `
		mutation {
			resendExternalUserInvite(id: "${id}"${customMessageParam})
		}
	`;

	const response: AxiosResponse = await axiosClient({
		url: 'graphql',
		method: 'POST',
		data: {
			query: graphqlQuery,
		},
	});
	return response?.data?.resendExternalUserInvite;
}

export async function verifyOnboardingToken(
	input: VerifyOnboardingTokenInput
): Promise<OnboardingTokenVerificationResponse> {
	const graphqlQuery = `
		{
			verifyOnboardingToken(
				input: ${stringifyReqObj(input)}
			) {
				valid
				message
				sessionToken
				expiresAt
				externalUser {
					${companyExternalUserROGraphQL}
				}
			}
		}
	`;

	const response: AxiosResponse = await baseAxiosClient({
		url: `${Config.apiBaseUrl}/graphql`,
		method: 'POST',
		data: {
			query: graphqlQuery,
		},
		headers: {
			'Content-Type': 'application/json',
		},
	});

	return (
		response?.data?.data?.verifyOnboardingToken ||
		response?.data?.verifyOnboardingToken
	);
}

export async function refreshOnboardingSession(
	sessionToken: string
): Promise<OnboardingTokenVerificationResponse> {
	const graphqlQuery = `
        mutation {
            refreshOnboardingSession {
                valid
                message
                sessionToken
                expiresAt
                externalUser {
                    ${companyExternalUserROGraphQL}
                }
            }
        }
    `;

	const response: AxiosResponse = await baseAxiosClient({
		url: `${Config.apiBaseUrl}/graphql`,
		method: 'POST',
		data: { query: graphqlQuery },
		headers: {
			'Content-Type': 'application/json',
			Authorization: `Bearer ${sessionToken}`,
		},
	});
	return (
		response?.data?.data?.refreshOnboardingSession ||
		response?.data?.refreshOnboardingSession
	);
}

export async function submitOnboardingForm(
	input: SubmitOnboardingFormInput
): Promise<OnboardingSubmissionResponse> {
	const graphqlQuery = `
		mutation {
			submitOnboardingForm(
				input: ${stringifyReqObj(input)}
			) {
				success
				message
				externalUser {
					${companyExternalUserROGraphQL}
				}
			}
		}
	`;

	const response: AxiosResponse = await baseAxiosClient({
		url: `${Config.apiBaseUrl}/graphql`,
		method: 'POST',
		data: {
			query: graphqlQuery,
		},
		headers: {
			'Content-Type': 'application/json',
		},
	});
	return (
		response?.data?.data?.submitOnboardingForm ||
		response?.data?.submitOnboardingForm
	);
}

export async function submitOnboardingFormExternal(
	input: SubmitExternalOnboardingFormInput,
	sessionToken: string
): Promise<OnboardingSubmissionResponse> {
	const graphqlQuery = `
		mutation {
			submitOnboardingFormExternal(
				input: ${stringifyReqObj(input)}
			) {
				success
				message
				externalUser {
					${companyExternalUserROGraphQL}
				}
			}
		}
	`;

	const response: AxiosResponse = await baseAxiosClient({
		url: `${Config.apiBaseUrl}/graphql`,
		method: 'POST',
		data: {
			query: graphqlQuery,
		},
		headers: {
			'Content-Type': 'application/json',
			Authorization: `Bearer ${sessionToken}`,
		},
	});
	return (
		response?.data?.data?.submitOnboardingFormExternal ||
		response?.data?.submitOnboardingFormExternal
	);
}

export async function updateOnboardingProgress(
	data: UpdateCompanyExternalUserDTO,
	sessionToken: string
): Promise<OnboardingProgressUpdateResponse> {
	const graphqlQuery = `
		mutation {
			updateOnboardingFormExternal(
				data: ${stringifyReqObj(data)}
			) {
				success
				message
				externalUser {
					${companyExternalUserROGraphQL}
				}
			}
		}
	`;

	const response: AxiosResponse = await baseAxiosClient({
		url: `${Config.apiBaseUrl}/graphql`,
		method: 'POST',
		data: {
			query: graphqlQuery,
		},
		headers: {
			'Content-Type': 'application/json',
			Authorization: `Bearer ${sessionToken}`,
		},
	});
	return (
		response?.data?.data?.updateOnboardingFormExternal ||
		response?.data?.updateOnboardingFormExternal
	);
}

const companyExternalUserROGraphQL = `
	id
	created
	updated
	firstName
	lastName
	preferredName
	preferredPronouns
	email
	phone
	dateOfBirth
	groups {
		id
		name
		description
		created
		updated
	}
	role
	status
	notes
	lastContactDate
	company {
		name
	}
	companyId
	userProfileId
	userProfile {
        ${userProfileROGraphQl}
    }
`;
